/**
 * Zwaluw — auto-rollende actie-tekst: seizoen-label + maand-deadline.
 *
 * Twee onafhankelijke rollover-ritmes:
 *   1. SEIZOEN-LABEL (springt 4× per jaar op 1 maart / juni / sept / dec)
 *      - Lente:  maart, april, mei
 *      - Zomer:  juni, juli, augustus
 *      - Herfst: september, oktober, november
 *      - Winter: december, januari, februari
 *
 *   2. DEADLINE (springt elke maand: "t/m {laatste dag} {maand}")
 *      - 1 mei  → "t/m 31 mei"
 *      - 1 juni → "t/m 30 juni"
 *      - 1 juli → "t/m 31 juli"
 *      - enz. — eind van huidige maand, schrikkeljaar-correct via Date(y, m+1, 0)
 *
 * Gebruikt in de hero deal-pill en de Hans Kazàn exit-popup zodat copy
 * automatisch meebeweegt zonder code-push.
 */

export interface SeizoensActie {
  /** "Lente-actie" / "Zomer-actie" / "Herfst-actie" / "Winter-actie" */
  label: string;
  /** Korte einddatum bv. "31 mei", "30 jun" */
  endShort: string;
  /** Volledige einddatum-zin bv. "t/m 31 mei", "t/m 30 juni" */
  endLong: string;
}

const MONTHS_LONG = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];
const MONTHS_SHORT = [
  "jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

/** Laatste dag van de maand van `now` — schrikkeljaar-correct. */
function lastDayOfMonth(now: Date): number {
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
}

function seasonLabel(month1to12: number): string {
  if (month1to12 >= 3 && month1to12 <= 5) return "Lente-actie";
  if (month1to12 >= 6 && month1to12 <= 8) return "Zomer-actie";
  if (month1to12 >= 9 && month1to12 <= 11) return "Herfst-actie";
  return "Winter-actie";
}

/** Bepaalt het huidige actie-blok: seizoen-label + eind-van-deze-maand deadline. */
export function getSeizoensActie(now: Date = new Date()): SeizoensActie {
  const m1to12 = now.getMonth() + 1;
  const dag = lastDayOfMonth(now);
  const maandLong = MONTHS_LONG[now.getMonth()] ?? "";
  const maandShort = MONTHS_SHORT[now.getMonth()] ?? "";
  return {
    label: seasonLabel(m1to12),
    endShort: `${dag} ${maandShort}`,
    endLong: `t/m ${dag} ${maandLong}`,
  };
}

// ---------------------------------------------------------------------------
// INRUILACTIE — Zwaluw heeft één centrale actie: € 2.100,- inruilkorting.
// Wordt gebruikt op alle landings (niche + city) en de homepage hero.
// ---------------------------------------------------------------------------

export interface ZwaluwInruilActie {
  /** Eyebrow-pill kleine caps, bv. "LENTE-ACTIE" */
  eyebrow: string;
  /** Grote title-tekst — komt na eyebrow */
  title: string;
  /** Sub-regel onder de title, accepteert {{deadline}} placeholder */
  subtitle: string;
  /** ISO 8601 datum (laatste dag van huidige maand om 23:59:59) — voor countdown */
  deadlineIso: string;
  /** Korte einddatum "31 mei" voor losse weergave */
  endShort: string;
}

/** Eind van huidige maand om 23:59:59 lokaal — voor de countdown-deadline. */
function endOfMonthIso(now: Date): string {
  const eom = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0, // dag 0 van volgende maand = laatste dag huidige
    23,
    59,
    59,
  );
  // Locale-onafhankelijke ISO zonder timezone-shift verrassingen
  const yyyy = eom.getFullYear();
  const mm = String(eom.getMonth() + 1).padStart(2, "0");
  const dd = String(eom.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T23:59:59`;
}

/**
 * Returnt de Zwaluw-inruilactie configuratie voor het huidige moment.
 * Seizoens-label rolt per kwartaal, deadline rolt per maand.
 * @param inruilAmount Bedrag string ("1.500" / "2.100") — wordt geinjecteerd
 *   in title. Default "2.100" voor backward-compat.
 */
export function getZwaluwInruilActie(now: Date = new Date(), inruilAmount: string = "2.100"): ZwaluwInruilActie {
  const actie = getSeizoensActie(now);
  return {
    eyebrow: actie.label.toUpperCase(),
    title: `Tot € ${inruilAmount},— inruilkorting`,
    subtitle: "op uw oude bad of douche · bij aanvraag vóór {{deadline}}",
    deadlineIso: endOfMonthIso(now),
    endShort: actie.endShort,
  };
}
